/**
 * AdminExtratosTab — Gestão de extratos no Supabase Storage
 * Sprint 6: Extraído de AdminPanel.jsx (self-contained — gerencia próprio state)
 * Sprint 10: Adicionado busca por nome de arquivo, paginação, KPIs melhorados.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { LIGHT, Card, STitle, Badge, fmtBytes, fmtDateStr, fileIcon } from "./adminUtils.jsx";
import { RADIUS } from "../shared/theme.js";
import { flexRow, flexBetween } from "../shared/styles.js";
import {
  fetchAllProfiles,
  listClientExtratos,
  getExtratoSignedUrl,
  deleteExtrato,
  logAudit,
} from "../supabaseClient.js";
import { parseExtrato } from "../extratoParser.js";
import { useToast } from "../components/Toast.jsx";
import EmptyState from "./components/EmptyState.jsx";

const PAGE_SIZE = 30;

export default function AdminExtratosTab({ setToast: _setToast, T }) {
  const toast = useToast();
  const [extratos, setExtratos] = useState([]);
  const [extratosLoading, setExtratosLoading] = useState(false);
  const [extratosError, setExtratosError] = useState(null);
  const [extratosFilter, setExtratosFilter] = useState("__all__");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [downloadingPath, setDownloadingPath] = useState(null);
  const [reprocessingPath, setReprocessingPath] = useState(null);
  const [reprocessResult, setReprocessResult] = useState(null);

  // ── Carrega todos os extratos do Storage ──
  const loadExtratos = useCallback(async () => {
    setExtratosLoading(true);
    setExtratosError(null);
    try {
      const profiles = await fetchAllProfiles();
      const clients = profiles.filter((p) => p.client_id);
      const allFiles = [];
      await Promise.all(
        clients.map(async (p) => {
          try {
            const files = await listClientExtratos(p.client_id);
            files.forEach((f) =>
              allFiles.push({
                ...f,
                clientId: p.client_id,
                clientName: p.full_name || p.email || p.client_id,
              })
            );
          } catch (_) {
            /* pasta ainda não existe */
          }
        })
      );
      allFiles.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setExtratos(allFiles);
    } catch (e) {
      setExtratosError(e.message);
    } finally {
      setExtratosLoading(false);
    }
  }, []);

  useEffect(() => {
    loadExtratos();
  }, [loadExtratos]);

  // ── Filtered + paginated ──
  const filtered = useMemo(() => {
    let list = extratos;

    if (extratosFilter !== "__all__") {
      list = list.filter((f) => f.clientId === extratosFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter(
        (f) =>
          (f.name || "").toLowerCase().includes(q) || (f.clientName || "").toLowerCase().includes(q)
      );
    }

    return list;
  }, [extratos, extratosFilter, search]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageExtratos = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Reset page on filter change
  const setSearchAndReset = (v) => {
    setSearch(v);
    setPage(0);
  };
  const setFilterAndReset = (v) => {
    setExtratosFilter(v);
    setPage(0);
  };

  // ── Handlers ──
  const handleDownloadExtrato = async (path, name) => {
    setDownloadingPath(path);
    try {
      const url = await getExtratoSignedUrl(path);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      toast(`Erro ao baixar: ${e.message}`, "error");
    } finally {
      setDownloadingPath(null);
    }
  };

  const handleReprocessExtrato = async (path, displayName) => {
    setReprocessingPath(path);
    try {
      const url = await getExtratoSignedUrl(path);
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Falha ao buscar arquivo (HTTP ${res.status})`);
      const blob = await res.blob();
      const file = new File([blob], displayName, { type: blob.type });
      const parsed = await parseExtrato(file);
      setReprocessResult({ path, name: displayName, ...parsed });
    } catch (e) {
      setReprocessResult({ path, name: displayName, ok: false, error: e.message });
    } finally {
      setReprocessingPath(null);
    }
  };

  const handleDeleteExtrato = async (path) => {
    if (!window.confirm("Excluir este arquivo permanentemente?")) return;
    try {
      await deleteExtrato(path);
      await logAudit("extrato_deleted", { path });
      setExtratos((prev) => prev.filter((f) => f.path !== path));
      toast("Arquivo excluído.", "success");
    } catch (e) {
      toast(`Erro ao excluir: ${e.message}`, "error");
    }
  };

  const inputStyle = {
    padding: "7px 12px",
    borderRadius: RADIUS.sm,
    border: `1px solid ${T.border}`,
    fontSize: 12,
    color: T.text,
    background: T.card,
    outline: "none",
  };
  const btnStyle = {
    padding: "6px 14px",
    borderRadius: RADIUS.sm,
    border: `1px solid ${T.border}`,
    background: T.bg,
    color: T.text,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  };

  return (
    <div>
      <Card T={T} style={{ marginBottom: 20 }}>
        <div
          style={{
            ...flexBetween,
            flexWrap: "wrap",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <STitle
            title="Arquivos Enviados pelos Clientes"
            sub="Extratos armazenados no Supabase Storage — bucket extratos"
            T={T}
          />
          <button
            onClick={loadExtratos}
            disabled={extratosLoading}
            style={{
              padding: "7px 14px",
              borderRadius: RADIUS.sm,
              border: "none",
              background: T.text,
              color: "#fff",
              fontSize: 12,
              cursor: "pointer",
              fontWeight: 600,
              opacity: extratosLoading ? 0.6 : 1,
            }}
          >
            {extratosLoading ? "⏳ Carregando…" : "↻ Atualizar"}
          </button>
        </div>

        {/* KPIs */}
        {!extratosLoading && !extratosError && (
          <div style={{ ...flexRow, gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
            {[
              { label: "Total de arquivos", v: extratos.length },
              { label: "Clientes com envios", v: new Set(extratos.map((f) => f.clientId)).size },
              {
                label: "Tamanho total",
                v: fmtBytes(extratos.reduce((s, f) => s + (f.size || 0), 0)),
              },
            ].map((s) => (
              <div
                key={s.label}
                style={{
                  background: T.bg,
                  border: `1px solid ${T.border}`,
                  borderRadius: RADIUS.md,
                  padding: "10px 18px",
                  minWidth: 140,
                }}
              >
                <div style={{ fontSize: 11, color: T.sub, marginBottom: 3 }}>{s.label}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>{s.v}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filter bar */}
        <div
          style={{
            ...flexRow,
            gap: 10,
            marginBottom: 14,
            flexWrap: "wrap",
          }}
        >
          <input
            type="text"
            placeholder="Buscar arquivo ou cliente…"
            value={search}
            onChange={(e) => setSearchAndReset(e.target.value)}
            style={{ ...inputStyle, flex: "1 1 180px", minWidth: 160 }}
          />
          <select
            value={extratosFilter}
            onChange={(e) => setFilterAndReset(e.target.value)}
            style={{
              ...inputStyle,
              cursor: "pointer",
              minWidth: 160,
            }}
          >
            <option value="__all__">Todos os clientes</option>
            {[...new Map(extratos.map((f) => [f.clientId, f.clientName])).entries()].map(
              ([id, name]) => (
                <option key={id} value={id}>
                  {name} ({id.slice(0, 8)})
                </option>
              )
            )}
          </select>
          {(search || extratosFilter !== "__all__") && (
            <span style={{ fontSize: 11, color: T.muted }}>
              {filtered.length} de {extratos.length}
            </span>
          )}
        </div>

        {extratosLoading && (
          <div style={{ textAlign: "center", padding: "40px 0", color: T.sub, fontSize: 13 }}>
            ⏳ Carregando arquivos do Storage…
          </div>
        )}
        {extratosError && (
          <div
            style={{
              background: "#FEE2E2",
              border: "1px solid #FECACA",
              borderRadius: RADIUS.md,
              padding: "14px 18px",
              color: "#DC2626",
              fontSize: 13,
            }}
          >
            ❌ Erro ao carregar: {extratosError}
          </div>
        )}
        {!extratosLoading && !extratosError && extratos.length === 0 && (
          <EmptyState
            icon="📭"
            title="Nenhum extrato enviado ainda"
            subtitle="Os arquivos aparecerão aqui após os clientes fazerem upload."
            T={T}
          />
        )}

        {!extratosLoading && !extratosError && extratos.length > 0 && (
          <>
            {filtered.length === 0 ? (
              <div style={{ textAlign: "center", padding: "30px 0", color: T.muted, fontSize: 13 }}>
                Nenhum arquivo corresponde aos filtros.
              </div>
            ) : (
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr>
                      {["Cliente", "Arquivo", "Tipo", "Tamanho", "Enviado em", "Ações"].map((h) => (
                        <th
                          key={h}
                          scope="col"
                          style={{
                            textAlign: "left",
                            padding: "8px 12px",
                            borderBottom: `2px solid ${T.border}`,
                            color: T.sub,
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageExtratos.map((f, i) => {
                      const displayName = f.name.replace(/^\d+_/, "");
                      return (
                        <tr key={f.path} style={{ background: i % 2 === 0 ? T.row1 : T.row2 }}>
                          <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                              {f.clientName}
                            </div>
                            <div style={{ fontSize: 10, color: T.muted }}>
                              {f.clientId.slice(0, 8)}
                            </div>
                          </td>
                          <td style={{ padding: "9px 12px" }}>
                            <div style={{ ...flexRow, gap: 6 }}>
                              <span style={{ fontSize: 16 }}>{fileIcon(f.type)}</span>
                              <span
                                style={{
                                  fontSize: 12,
                                  maxWidth: 220,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  color: T.text,
                                }}
                                title={displayName}
                              >
                                {displayName}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: "9px 12px" }}>
                            <Badge
                              text={f.type?.split("/")[1]?.toUpperCase() || "FILE"}
                              color={T.info || "#2563EB"}
                            />
                          </td>
                          <td style={{ padding: "9px 12px", color: T.sub, whiteSpace: "nowrap" }}>
                            {fmtBytes(f.size)}
                          </td>
                          <td
                            style={{
                              padding: "9px 12px",
                              color: T.sub,
                              whiteSpace: "nowrap",
                              fontSize: 12,
                            }}
                          >
                            {fmtDateStr(f.createdAt)}
                          </td>
                          <td style={{ padding: "9px 12px" }}>
                            <div style={{ ...flexRow, gap: 8 }}>
                              <button
                                onClick={() => handleDownloadExtrato(f.path, displayName)}
                                disabled={downloadingPath === f.path}
                                style={{
                                  padding: "5px 12px",
                                  borderRadius: RADIUS.sm,
                                  border: "none",
                                  background: T.accent || "#2563EB",
                                  color: "#fff",
                                  fontSize: 11,
                                  cursor: "pointer",
                                  fontWeight: 600,
                                  opacity: downloadingPath === f.path ? 0.6 : 1,
                                }}
                              >
                                {downloadingPath === f.path ? "…" : "⬇ Baixar"}
                              </button>
                              <button
                                onClick={() => handleReprocessExtrato(f.path, displayName)}
                                disabled={reprocessingPath === f.path}
                                title="Reprocessar: faz novo parse e exibe resultado"
                                style={{
                                  padding: "5px 10px",
                                  borderRadius: RADIUS.sm,
                                  border: "1px solid #D1FAE5",
                                  background: "#ECFDF5",
                                  color: "#059669",
                                  fontSize: 11,
                                  cursor: "pointer",
                                  fontWeight: 600,
                                  opacity: reprocessingPath === f.path ? 0.6 : 1,
                                }}
                              >
                                {reprocessingPath === f.path ? "⏳" : "♻"}
                              </button>
                              <button
                                onClick={() => handleDeleteExtrato(f.path)}
                                style={{
                                  padding: "5px 10px",
                                  borderRadius: RADIUS.sm,
                                  border: "1px solid #FECACA",
                                  background: "#FEF2F2",
                                  color: "#DC2626",
                                  fontSize: 11,
                                  cursor: "pointer",
                                  fontWeight: 600,
                                }}
                              >
                                🗑
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                style={{
                  ...flexRow,
                  gap: 8,
                  justifyContent: "center",
                  marginTop: 14,
                  paddingTop: 12,
                  borderTop: `1px solid ${T.border}`,
                }}
              >
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  style={{ ...btnStyle, opacity: page === 0 ? 0.4 : 1 }}
                >
                  ← Anterior
                </button>
                <span style={{ fontSize: 12, color: T.sub }}>
                  {page + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  style={{ ...btnStyle, opacity: page >= totalPages - 1 ? 0.4 : 1 }}
                >
                  Próxima →
                </button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Modal resultado de reprocessamento */}
      {reprocessResult && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 999,
            ...flexRow,
            justifyContent: "center",
            padding: 20,
          }}
          onClick={() => setReprocessResult(null)}
        >
          <div
            style={{
              background: T.card,
              borderRadius: RADIUS.lg,
              padding: 28,
              maxWidth: 560,
              width: "100%",
              boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
              maxHeight: "80vh",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                ...flexBetween,
                marginBottom: 16,
              }}
            >
              <h3 style={{ fontSize: 16, fontWeight: 800, color: T.text, margin: 0 }}>
                ♻ Reprocessamento — {reprocessResult.name}
              </h3>
              <button
                onClick={() => setReprocessResult(null)}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: 20,
                  color: T.muted,
                  cursor: "pointer",
                }}
              >
                ✕
              </button>
            </div>

            {reprocessResult.ok ? (
              <>
                <div
                  style={{
                    background: "#DCFCE7",
                    border: "1px solid #BBF7D0",
                    borderRadius: 10,
                    padding: "10px 16px",
                    marginBottom: 16,
                    fontSize: 13,
                    color: "#166534",
                    fontWeight: 600,
                  }}
                >
                  ✅ Parse concluído · Broker detectado: <strong>{reprocessResult.broker}</strong>
                </div>
                {reprocessResult.warnings?.length > 0 && (
                  <div
                    style={{
                      background: "#FEF9C3",
                      border: "1px solid #FDE68A",
                      borderRadius: 10,
                      padding: "10px 16px",
                      marginBottom: 16,
                      fontSize: 12,
                      color: "#854D0E",
                    }}
                  >
                    ⚠️ {reprocessResult.warnings.join(" · ")}
                  </div>
                )}
                {reprocessResult.data && (
                  <div style={{ fontSize: 12, color: T.sub, lineHeight: 1.8 }}>
                    {reprocessResult.data.patrimonio_total != null && (
                      <div>
                        💰 Patrimônio BRL:{" "}
                        <strong style={{ color: T.text }}>
                          R${" "}
                          {reprocessResult.data.patrimonio_total.toLocaleString("pt-BR", {
                            minimumFractionDigits: 2,
                          })}
                        </strong>
                      </div>
                    )}
                    {reprocessResult.data.patrimonio_usd != null && (
                      <div>
                        💵 Patrimônio USD:{" "}
                        <strong style={{ color: T.text }}>
                          ${" "}
                          {reprocessResult.data.patrimonio_usd.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                          })}
                        </strong>
                      </div>
                    )}
                    {reprocessResult.data.ret_month != null && (
                      <div>
                        📈 Retorno mês:{" "}
                        <strong style={{ color: T.text }}>
                          {reprocessResult.data.ret_month.toFixed(2).replace(".", ",")}%
                        </strong>
                      </div>
                    )}
                    {reprocessResult.data.positions?.length > 0 && (
                      <div>
                        📋 Posições detectadas:{" "}
                        <strong style={{ color: T.text }}>
                          {reprocessResult.data.positions.length}
                        </strong>
                      </div>
                    )}
                    <details style={{ marginTop: 10 }}>
                      <summary style={{ cursor: "pointer", color: T.sub, fontSize: 11 }}>
                        Ver JSON completo do parse
                      </summary>
                      <pre
                        style={{
                          background: T.bg,
                          borderRadius: 8,
                          padding: 10,
                          marginTop: 6,
                          fontSize: 10,
                          overflow: "auto",
                          maxHeight: 200,
                          color: T.text,
                        }}
                      >
                        {JSON.stringify(reprocessResult.data, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}
              </>
            ) : (
              <div
                style={{
                  background: "#FEE2E2",
                  border: "1px solid #FECACA",
                  borderRadius: 10,
                  padding: "12px 16px",
                  fontSize: 13,
                  color: "#DC2626",
                }}
              >
                ❌ Erro no reprocessamento: {reprocessResult.error || "Erro desconhecido"}
              </div>
            )}

            <div style={{ marginTop: 20, textAlign: "right" }}>
              <button
                onClick={() => setReprocessResult(null)}
                style={{
                  padding: "8px 20px",
                  borderRadius: 8,
                  border: "none",
                  background: T.border,
                  color: T.text,
                  fontSize: 13,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

AdminExtratosTab.propTypes = {
  setToast: PropTypes.func.isRequired,
  T: PropTypes.object.isRequired,
};
